import {Directive, ElementRef, Renderer, OnInit, HostBinding, Input, HostListener} from '@angular/core';
import {WindowRefService} from './shared/window-ref-service.service';

declare var window: any;

@Directive({
  selector: '[wow]'
})
export class WowDirective implements OnInit {
  constructor(
    public elementRef: ElementRef,
    private renderer: Renderer,
    private windowRef: WindowRefService
  ) {
  }

  @HostBinding('class.wow') isWowClassActive = true;

  //#region animation
  @Input('wow-animation') animationXs: string;
  @Input('wow-animation-sm') animationSm: string;
  @Input('wow-animation-md') animationMd: string;
  @Input('wow-animation-lg') animationLg: string;
  @Input('wow-animation-xl') animationXl: string;
  @Input('wow-animation-mob') animationMobXs: string;
  @Input('wow-animation-mob-sm') animationMobSm: string;
  @Input('wow-animation-mob-md') animationMobMd: string;
  @Input('wow-animation-mob-lg') animationMobLg: string;
  @Input('wow-animation-mob-xl') animationMobXl: string;

  get animation(): string {
    if (WowDirective.isMobile) {
      return this._calcAnimationMob() || this._calcAnimation();
    } else {
      return this._calcAnimation();
    }
  }

  private _calcAnimation() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.animationXs || null;
      }
      case ViewportRange.sm: {
        return this.animationSm || this.animationXs || null;
      }
      case ViewportRange.md: {
        return this.animationMd || this.animationSm || this.animationXs || null;
      }
      case ViewportRange.lg: {
        return this.animationLg || this.animationMd || this.animationSm || this.animationXs || null;
      }
      case ViewportRange.xl: {
        return this.animationXl || this.animationLg || this.animationMd || this.animationSm || this.animationXs || null;
      }
      default: {
        return null;
      }
    }
  }

  private _calcAnimationMob() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.animationMobXs || null;
      }
      case ViewportRange.sm: {
        return this.animationMobSm || this.animationMobXs || null;
      }
      case ViewportRange.md: {
        return this.animationMobMd || this.animationMobSm || this.animationMobXs || null;
      }
      case ViewportRange.lg: {
        return this.animationMobLg || this.animationMobMd || this.animationMobSm || this.animationMobXs || null;
      }
      case ViewportRange.xl: {
        return this.animationMobXl || this.animationMobLg || this.animationMobMd || this.animationMobSm || this.animationMobXs || null;
      }
      default: {
        return null;
      }
    }
  }

  //#endregion animation

  //#region duration
  @Input('wow-duration') durationXs: string;
  @Input('wow-duration-sm') durationSm: string;
  @Input('wow-duration-md') durationMd: string;
  @Input('wow-duration-lg') durationLg: string;
  @Input('wow-duration-xl') durationXl: string;
  @Input('wow-duration-mob') durationMobXs: string;
  @Input('wow-duration-mob-sm') durationMobSm: string;
  @Input('wow-duration-mob-md') durationMobMd: string;
  @Input('wow-duration-mob-lg') durationMobLg: string;
  @Input('wow-duration-mob-xl') durationMobXl: string;

  @HostBinding('attr.data-wow-duration') get duration(): string {
    if (WowDirective.isMobile) {
      return this._calcDurationMob() || this._calcDuration();
    } else {
      return this._calcDuration();
    }
  }

  private _calcDuration() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.durationXs || null;
      }
      case ViewportRange.sm: {
        return this.durationSm || this.durationXs || null;
      }
      case ViewportRange.md: {
        return this.durationMd || this.durationSm || this.durationXs || null;
      }
      case ViewportRange.lg: {
        return this.durationLg || this.durationMd || this.durationSm || this.durationXs || null;
      }
      case ViewportRange.xl: {
        return this.durationXl || this.durationLg || this.durationMd || this.durationSm || this.durationXs || null;
      }
      default: {
        return null;
      }
    }
  }

  private _calcDurationMob() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.durationMobXs || null;
      }
      case ViewportRange.sm: {
        return this.durationMobSm || this.durationMobXs || null;
      }
      case ViewportRange.md: {
        return this.durationMobMd || this.durationMobSm || this.durationMobXs || null;
      }
      case ViewportRange.lg: {
        return this.durationMobLg || this.durationMobMd || this.durationMobSm || this.durationMobXs || null;
      }
      case ViewportRange.xl: {
        return this.durationMobXl || this.durationMobLg || this.durationMobMd || this.durationMobSm || this.durationMobXs || null;
      }
      default: {
        return null;
      }
    }
  }

  //#endregion duration

  //#region delay
  @Input('wow-delay') delayXs: string;
  @Input('wow-delay-sm') delaySm: string;
  @Input('wow-delay-md') delayMd: string;
  @Input('wow-delay-lg') delayLg: string;
  @Input('wow-delay-xl') delayXl: string;
  @Input('wow-delay-mob') delayMobXs: string;
  @Input('wow-delay-mob-sm') delayMobSm: string;
  @Input('wow-delay-mob-md') delayMobMd: string;
  @Input('wow-delay-mob-lg') delayMobLg: string;
  @Input('wow-delay-mob-xl') delayMobXl: string;

  @HostBinding('attr.data-wow-delay') get delay(): string {
    if (WowDirective.isMobile) {
      return this._calcDelayMob() || this._calcDelay();
    } else {
      return this._calcDelay();
    }
  }

  private _calcDelay() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.delayXs || null;
      }
      case ViewportRange.sm: {
        return this.delaySm || this.delayXs || null;
      }
      case ViewportRange.md: {
        return this.delayMd || this.delaySm || this.delayXs || null;
      }
      case ViewportRange.lg: {
        return this.delayLg || this.delayMd || this.delaySm || this.delayXs || null;
      }
      case ViewportRange.xl: {
        return this.delayXl || this.delayLg || this.delayMd || this.delaySm || this.delayXs || null;
      }
      default: {
        return null;
      }
    }
  }

  private _calcDelayMob() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.delayMobXs || null;
      }
      case ViewportRange.sm: {
        return this.delayMobSm || this.delayMobXs || null;
      }
      case ViewportRange.md: {
        return this.delayMobMd || this.delayMobSm || this.delayMobXs || null;
      }
      case ViewportRange.lg: {
        return this.delayMobLg || this.delayMobMd || this.delayMobSm || this.delayMobXs || null;
      }
      case ViewportRange.xl: {
        return this.delayMobXl || this.delayMobLg || this.delayMobMd || this.delayMobSm || this.delayMobXs || null;
      }
      default: {
        return null;
      }
    }
  }

  //#endregion delay

  //#region offset
  @Input('wow-offset') offsetXs: string;
  @Input('wow-offset-sm') offsetSm: string;
  @Input('wow-offset-md') offsetMd: string;
  @Input('wow-offset-lg') offsetLg: string;
  @Input('wow-offset-xl') offsetXl: string;
  @Input('wow-offset-mob') offsetMobXs: string;
  @Input('wow-offset-mob-sm') offsetMobSm: string;
  @Input('wow-offset-mob-md') offsetMobMd: string;
  @Input('wow-offset-mob-lg') offsetMobLg: string;
  @Input('wow-offset-mob-xl') offsetMobXl: string;

  @HostBinding('attr.data-wow-offset') get offset(): string {
    if (WowDirective.isMobile) {
      return this._calcOffsetMob() || this._calcOffset();
    } else {
      return this._calcOffset();
    }
  }

  private _calcOffset() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.offsetXs || null;
      }
      case ViewportRange.sm: {
        return this.offsetSm || this.offsetXs || null;
      }
      case ViewportRange.md: {
        return this.offsetMd || this.offsetSm || this.offsetXs || null;
      }
      case ViewportRange.lg: {
        return this.offsetLg || this.offsetMd || this.offsetSm || this.offsetXs || null;
      }
      case ViewportRange.xl: {
        return this.offsetXl || this.offsetLg || this.offsetMd || this.offsetSm || this.offsetXs || null;
      }
      default: {
        return null;
      }
    }
  }

  private _calcOffsetMob() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.offsetMobXs || null;
      }
      case ViewportRange.sm: {
        return this.offsetMobSm || this.offsetMobXs || null;
      }
      case ViewportRange.md: {
        return this.offsetMobMd || this.offsetMobSm || this.offsetMobXs || null;
      }
      case ViewportRange.lg: {
        return this.offsetMobLg || this.offsetMobMd || this.offsetMobSm || this.offsetMobXs || null;
      }
      case ViewportRange.xl: {
        return this.offsetMobXl || this.offsetMobLg || this.offsetMobMd || this.offsetMobSm || this.offsetMobXs || null;
      }
      default: {
        return null;
      }
    }
  }

  //#endregion offset

  //#region iteration
  @Input('wow-iteration') iterationXs: string;
  @Input('wow-iteration-sm') iterationSm: string;
  @Input('wow-iteration-md') iterationMd: string;
  @Input('wow-iteration-lg') iterationLg: string;
  @Input('wow-iteration-xl') iterationXl: string;
  @Input('wow-iteration-mob') iterationMobXs: string;
  @Input('wow-iteration-mob-sm') iterationMobSm: string;
  @Input('wow-iteration-mob-md') iterationMobMd: string;
  @Input('wow-iteration-mob-lg') iterationMobLg: string;
  @Input('wow-iteration-mob-xl') iterationMobXl: string;

  @HostBinding('attr.data-wow-iteration') get iteration(): string {
    if (WowDirective.isMobile) {
      return this._calcIterationMob() || this._calcIteration();
    } else {
      return this._calcIteration();
    }
  }

  private _calcIteration() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.iterationXs || null;
      }
      case ViewportRange.sm: {
        return this.iterationSm || this.iterationXs || null;
      }
      case ViewportRange.md: {
        return this.iterationMd || this.iterationSm || this.iterationXs || null;
      }
      case ViewportRange.lg: {
        return this.iterationLg || this.iterationMd || this.iterationSm || this.iterationXs || null;
      }
      case ViewportRange.xl: {
        return this.iterationXl || this.iterationLg || this.iterationMd || this.iterationSm || this.iterationXs || null;
      }
      default: {
        return null;
      }
    }
  }

  private _calcIterationMob() {
    switch (this.viewportRange) {
      case ViewportRange.xs: {
        return this.iterationMobXs || null;
      }
      case ViewportRange.sm: {
        return this.iterationMobSm || this.iterationMobXs || null;
      }
      case ViewportRange.md: {
        return this.iterationMobMd || this.iterationMobSm || this.iterationMobXs || null;
      }
      case ViewportRange.lg: {
        return this.iterationMobLg || this.iterationMobMd || this.iterationMobSm || this.iterationMobXs || null;
      }
      case ViewportRange.xl: {
        return this.iterationMobXl || this.iterationMobLg || this.iterationMobMd || this.iterationMobSm || this.iterationMobXs || null;
      }
      default: {
        return null;
      }
    }
  }

  //#endregion iteration

  //#region windowWidth
  private _windowWidth: number;
  private get windowWidth(): number {
    return this._windowWidth;
  }

  private set windowWidth(value: number) {
    this._windowWidth = value;
    this.viewportRange = ViewportRange.forWidth(value);
  }

  //#endregion

  //#region viewportRange
  private _viewportRange: ViewportRange;
  private get viewportRange(): ViewportRange {
    return this._viewportRange;
  }

  private set viewportRange(value: ViewportRange) {
    const oldAnimation = this.animation;
    this._viewportRange = value;
    const newAnimation = this.animation;

    if (oldAnimation != newAnimation) {
      if (oldAnimation) {
        this.renderer.setElementClass(this.elementRef.nativeElement, oldAnimation, /*isAdd*/ false);
      }
      if (newAnimation) {
        this.renderer.setElementClass(this.elementRef.nativeElement, newAnimation, /*isAdd*/ true);
      }
    }
  }

  //#endregion

  ngOnInit(): void {
    this.updateWindowWidth();
  }

  @HostListener('window:load')
  @HostListener('window:resize')
  updateWindowWidth() {
    if (!this.windowRef.nativeWindow) {
      return;
    }
    this.windowWidth = this.windowRef.nativeWindow.innerWidth;
  }

  private static _isMobile: boolean;
  private static get isMobile(): boolean {
    if (WowDirective._isMobile === undefined) {
      WowDirective._isMobile = WowDirective.calcIsMobile();
    }

    return WowDirective._isMobile;
  }

  private static calcIsMobile(): boolean {
    let check = false; // TODO extract this

    (function (a) {
      if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) {
        check = true;
      }
    })(navigator.userAgent || navigator.vendor || window.opera);

    return check;
  }
}

// TODO move to proper place
export enum ViewportRange {
  xs,
  sm,
  md,
  lg,
  xl,
}

export namespace ViewportRange {

  export function forWidth(width: number): ViewportRange {
    if (!width) {
      return null;
    }

    if (width < 576) {
      return ViewportRange.xs;
    }
    if (width < 768) {
      return ViewportRange.sm;
    }
    if (width < 992) {
      return ViewportRange.md;
    }
    if (width < 1200) {
      return ViewportRange.lg;
    }

    return ViewportRange.xl;
  }
}



// WEBPACK FOOTER //
// C:/Projects/dod/companyintroduction/src/app/wow.directive.ts